import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import queryString from 'query-string';
import { Filters, Pagination, Response } from '@ui/src';
import {
  NewApplicationsValidatorResponse,
  NewApplicationListItem,
  NewApplicationCorporateInfo,
  ApplicationType,
  Channel,
  ApplicationAgreement,
  AllowedCategory,
} from '../types';
import { applicationsApi } from './services';
import { Employee } from '../../../store/dictionaries/types';
import { LockAuditLog, Lock } from '../../individual/types';
import { CustomerScore } from '../../customers/types';

import ROUTES from '../../../routes';

export interface NewApplicationListState {
  isLoading: boolean;
  requestInProgress: boolean;
  newApplications?: NewApplicationListItem[];
  sort?: {
    sortProp: string;
    sortDir: string;
  };
  filters?: Filters;
  pagination?: Pagination;
  validators?: NewApplicationsValidatorResponse;
  currentEmployee?: Employee;
  applicationChat?: Channel[];
  agreement?: ApplicationAgreement;
  allowedDocumentsCategories?: AllowedCategory[];
  auditLogs?: LockAuditLog[];
  auditLogsPagination?: Pagination;
  locks?: Lock[];
  locksPagination?: Pagination;
  activeLock?: Lock;
  lockItem?: Lock;
  lockItemAuditLogs?: LockAuditLog[];
  lockItemAuditLogsPagination?: Pagination;
  amlChangeLogs?: CustomerScore[];
  amlChangeLogsPagination?: Pagination;
}

const initialState: NewApplicationListState = {
  isLoading: false,
  newApplications: undefined,
  sort: undefined,
  requestInProgress: false,
  filters: undefined,
};

export const newAppliationListSlice = createSlice({
  name: 'newAppliationList',
  initialState,
  reducers: {
    clearApplicationsList: (state) => {
      state.newApplications = undefined;
    },
    clearApplicationAgreementState: (state) => {
      state.agreement = undefined;
      state.allowedDocumentsCategories = undefined;
    },
    clearDetailsPanelState: (state) => {
      state.auditLogs = undefined;
      state.auditLogsPagination = undefined;
      state.locks = undefined;
      state.locksPagination = undefined;
      state.activeLock = undefined;
    },
    clearLockPanelState: (state) => {
      state.lockItem = undefined;
      state.lockItemAuditLogs = undefined;
      state.lockItemAuditLogsPagination = undefined;
    },
    clearAmlChangeLogs: (state) => {
      state.amlChangeLogs = undefined;
      state.amlChangeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      applicationsApi.endpoints.getNewApplicationsList.matchFulfilled,
      (state, action: PayloadAction<Response<NewApplicationListItem>>) => {
        state.newApplications = action.payload.data.edges.map((item) => {
          const result = { ...item.node };

          let route = ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO;

          if (result.type === ApplicationType.CORPORATE) {
            route = ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO;
          } else if (result.type === ApplicationType.COMPANY_OFFICIAL) {
            route = ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL;
          }

          const link = generatePath(route, {
            applicationId: result.id,
          });

          result.url = link;

          return result;
        });

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationsAMLScoresHistory.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<CustomerScore>> & {
          meta: { arg: { originalArgs: { query?: string } } };
        },
      ) => {
        let nextAmlChangeLogs: CustomerScore[] = [];

        if (
          action.meta.arg.originalArgs?.query?.includes('cursor') &&
          state.amlChangeLogs
        ) {
          nextAmlChangeLogs = ([] as CustomerScore[]).concat(
            state.amlChangeLogs,
          );
        }

        action.payload.data.edges?.forEach((item) => {
          nextAmlChangeLogs.push(item.node);
        });

        state.amlChangeLogs = nextAmlChangeLogs?.map((item, index) => ({
          ...item,
          order: index + 1,
        }));

        state.amlChangeLogsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCustomersCurrentEmployee.matchFulfilled,
      (state, action: PayloadAction<{ data: Employee }>) => {
        state.currentEmployee = action.payload.data;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationChat.matchFulfilled,
      (state, action: PayloadAction<Response<Channel>>) => {
        state.applicationChat = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationAgreement.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationAgreement }>) => {
        state.agreement = action.payload.data;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationClientAuditLogs.matchFulfilled,
      (state, action: PayloadAction<Response<LockAuditLog>>) => {
        state.auditLogs = action.payload.data.edges.map((item) => item.node);

        state.auditLogsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationClientLocks.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<Lock>> & {
          meta: { arg: { originalArgs?: string } };
        },
      ) => {
        state.locks = action.payload.data.edges.map((item) => ({
          ...item.node,
          type: item.node.createdByEmployeeId ? 'by Employee' : 'Auto',
        }));

        state.locksPagination = action.payload.data.pageInfo;

        if (action.meta?.arg?.originalArgs) {
          const parsed = queryString.parse(action.meta?.arg?.originalArgs);

          if (
            parsed.locked &&
            parsed.locked === 'true' &&
            state.activeLock &&
            !action.payload.data.edges.length
          ) {
            state.activeLock = undefined;
          }
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getNewApplicationsValidators.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: NewApplicationsValidatorResponse }>,
      ) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getNewApplicationDefaultDocumentCategories
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AllowedCategory[] }>) => {
        state.allowedDocumentsCategories = action.payload.data;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateIndividualApplicationStatus
        .matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        const { data } = action.payload;

        if (state.newApplications) {
          const nextNewApplications = state.newApplications.map((item) => {
            const result = { ...item };

            if (result.id === data.id) {
              result.reviewStatus = data.reviewStatus;
              result.status = data.status;
            }

            return result;
          });

          state.newApplications = nextNewApplications;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateCorporateApplicationStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationCorporateInfo }>) => {
        const { data } = action.payload;

        if (state.newApplications) {
          const nextNewApplications = state.newApplications.map((item) => {
            const result = { ...item };

            if (result.id === data.id) {
              result.reviewStatus = data.reviewStatus;
              result.status = data.status;
            }

            return result;
          });

          state.newApplications = nextNewApplications;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationClientLockItem.matchFulfilled,
      (state, action: PayloadAction<{ data: Lock }>) => {
        state.lockItem = action.payload.data;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateApplicationClientLock.matchFulfilled,
      (state, action: PayloadAction<{ data: Lock }>) => {
        state.locks = state.locks?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = {
              ...result,
              ...action.payload.data,
            };
          }

          return result;
        });

        state.lockItem = action.payload.data;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationClientLockItemAuditLogs
        .matchFulfilled,
      (state, action: PayloadAction<Response<LockAuditLog>>) => {
        if (state.lockItem) {
          state.lockItemAuditLogs = action.payload.data.edges.map(
            (item) => item.node,
          );

          state.lockItemAuditLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationClientActiveLocks.matchFulfilled,
      (state, action: PayloadAction<Response<Lock>>) => {
        if (action?.payload?.data?.edges?.length) {
          state.activeLock = action.payload.data.edges[0].node;
        } else {
          state.activeLock = undefined;
        }
      },
    );
  },
});

export const {
  clearApplicationsList,
  clearApplicationAgreementState,
  clearLockPanelState,
  clearDetailsPanelState,
  clearAmlChangeLogs,
} = newAppliationListSlice.actions;

export default newAppliationListSlice.reducer;
